import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useEventListener} from 'expo'
import {AppBskyEmbedVideo, moderatePost} from '@atproto/api'
import {
  CommonActions,
  useFocusEffect,
  useIsFocused,
} from '@react-navigation/native'

import {CAPSULE_FEED_URI} from '#/lib/constants'
import {CAPSULE_COVER_MAX_SECONDS} from '#/lib/coverTrim'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {getMasterVolume, getMuted, subscribeMaster} from '#/lib/sounds'
import {useFeedFeedback} from '#/state/feed-feedback'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useCapsuleByPostQuery} from '#/state/queries/capsule'
import {useFeedInfo} from '#/state/queries/feed'
import {usePostQuery} from '#/state/queries/post'
import {usePostFeedQuery} from '#/state/queries/post-feed'
import {useSession} from '#/state/session'
import {IS_WEB} from '#/env'
import {type CapsuleModel, type Slide} from '#/types/capsule'
import {
  type CapsuleOverlayMode,
  type CapsuleSkipStage,
} from './components/CapsuleOverlay'
import {type SeekDelta} from './components/TransportLayer'
import {type MediaEngine, useMediaEngine} from './engine'
import {INTERACTIVE_SELECTOR, isPagerLocked} from './pagerPolicy'
import {type FeedVideo} from './types'

// The skip control is always visible during playback (mode B): a
// YouTube-ad-style countdown badge (seconds remaining until 75% of the
// video), which becomes an active "Skip" pill once 75% is reached.
const SKIP_THRESHOLD = 0.75
// Seconds before the feed auto-scrolls to the next capsule when the user
// never pressed Play and the video ended.
const HINT_AUTOSCROLL_SECONDS = 5

type Props = NativeStackScreenProps<CommonNavigatorParams, 'CapsulePlay'>

/**
 * Capsule player controller — the single owner of the app-level policy:
 * feed resolution, pager index, the mode A/B presentation state (existing
 * semantics: Play switches the presentation of the SAME capsule, never
 * navigation), skip countdown, URL sync, and every transport action. Media
 * element/player mechanics live in the engine; the shell renders this.
 *
 * Ownership map (capsule player):
 *  - engine.ts                  media element/player + platform seam
 *  - controller.ts              feed/index/mode/navigation policy (this file)
 *  - components/PageChrome.tsx  active-page chrome presentation
 *  - index.tsx                  thin shell: layout, pager, prompt/quiz, arrows
 */
export type CapsulePlayController = {
  engine: MediaEngine
  videos: FeedVideo[]
  activeVideo: FeedVideo | undefined
  capsuleModel: CapsuleModel | null
  currentIndex: number
  capsuleFoundInFeed: boolean
  isPlayingMode: boolean
  isShowingQuiz: boolean
  isPromptOpen: boolean
  isRawCapsuleFetched: boolean
  pagerLocked: boolean
  playerHasNoVideo: boolean
  autoAdvancePossible: boolean
  hasNextPage: boolean
  feedFeedback: ReturnType<typeof useFeedFeedback>
  videoPaused: boolean
  hintSeconds: number | null
  overlayMode: CapsuleOverlayMode
  skipStage: CapsuleSkipStage
  skipCountdown: number
  skipProgress: number
  progress: number
  /** False while the active video cannot play yet — shows the loading overlay. */
  videoReady: boolean
  /**
   * True while the page plays muted: the web start-muted policy (scroll pages
   * before Play) OR a global mute set anywhere (quiz/volume control).
   */
  mutedPolicy: boolean
  onSeek: (delta: SeekDelta) => void
  onTogglePause: () => void
  onLongPressChange: (active: boolean) => void
  onPlayFromEnded: () => void
  onSkip: () => void
  onPressPlayCapsule: () => void
  onAudioStateChange: (state: {muted: boolean; volume: number}) => void
  onPagerPageChange: (index: number) => void
  onNextCapsule: () => void
  onPreviousCapsule: () => void
  pauseAndAsk: () => void
  resumeVideo: () => void
  exitPlayer: () => void
  closeQuiz: () => void
  escapeToFeed: () => void
  /** Called by VideoItem when the HLS stream fails fatally (e.g. CORS). Drops
      the loading overlay so the VideoItem's own error message becomes visible. */
  onVideoError: () => void
}

export function useCapsulePlayController(
  route: Props['route'],
  navigation: Props['navigation'],
): CapsulePlayController {
  const {capsuleid, autoPlay, authorDid, source} = route.params
  const fromTimeline = source === 'timeline'
  const {hasSession} = useSession()
  const moderationOpts = useModerationOpts()
  const isFocused = useIsFocused()

  // ── Position continuity ───────────────────────────────────────────────────
  // Opening a capsule from the grid feed resumes the muted preview that the
  // card was already playing (route.params.startTime) instead of 0:00. The
  // pending value is consumed — or discarded as invalid — exactly once.
  const openingUri =
    authorDid && capsuleid
      ? `at://${authorDid}/app.bsky.feed.post/${capsuleid}`
      : null
  const pendingStartTime =
    typeof route.params?.startTime === 'number' &&
    Number.isFinite(route.params.startTime) &&
    route.params.startTime > 0
      ? route.params.startTime
      : null

  const feedDesc = `feedgen|${CAPSULE_FEED_URI}` as const
  const {data: feedInfo} = useFeedInfo(CAPSULE_FEED_URI)
  const feedFeedback = useFeedFeedback(feedInfo ?? undefined, hasSession)
  const {data, hasNextPage, fetchNextPage} = usePostFeedQuery(feedDesc)

  const videos = useMemo(() => {
    const vids =
      data?.pages.flatMap(page => {
        const items: FeedVideo[] = []
        for (const slice of page.slices) {
          const feedPost = slice.items.find(
            item => item.uri === slice.feedPostUri,
          )
          if (feedPost && AppBskyEmbedVideo.isView(feedPost.post.embed)) {
            items.push({
              _reactKey: feedPost._reactKey,
              moderation: feedPost.moderation,
              post: feedPost.post,
              video: feedPost.post.embed,
              capsule: feedPost.capsule,
              feedContext: slice.feedContext,
              reqId: slice.reqId,
            })
          }
        }
        return items
      }) ?? []
    return vids
  }, [data])

  // Fetch the specific capsule post directly using the author DID.
  // This is the fast path: shows the correct capsule immediately without
  // waiting for the capsule feed to load and paginate to the right item.
  const postAtUri =
    authorDid && capsuleid
      ? `at://${authorDid}/app.bsky.feed.post/${capsuleid}`
      : undefined
  const {data: directPost, isFetched: isDirectPostFetched} =
    usePostQuery(postAtUri)
  const directActiveVideo = useMemo(() => {
    if (!directPost || !AppBskyEmbedVideo.isView(directPost.embed))
      return undefined
    return {
      _reactKey: directPost.uri,
      post: directPost,
      // The $Typed brand from the query response isn't assignable to
      // VideoItem's plain View — same runtime object.
      video: directPost.embed,
      capsule: (directPost as unknown as Record<string, unknown>).capsule as
        Record<string, unknown> | undefined,
      moderation: moderationOpts
        ? moderatePost(directPost, moderationOpts)
        : undefined,
      feedContext: undefined,
      reqId: undefined,
    }
  }, [directPost, moderationOpts])

  // ── Pager index resolution ────────────────────────────────────────────────
  // found=false → the only page is the direct post (timeline opens, or the
  // capsule is not (yet) confirmed in the feed). found=true → pages are a
  // window of the capsule feed around `currentIndex`.
  const [currentIndex, setCurrentIndex] = useState(0)
  const currentIndexRef = useRef(0)
  useEffect(() => {
    currentIndexRef.current = currentIndex
  }, [currentIndex])
  const [indexResolved, setIndexResolved] = useState(!capsuleid || fromTimeline)
  const [capsuleFoundInFeed, setCapsuleFoundInFeed] = useState(false)
  const feedActiveVideo = capsuleFoundInFeed ? videos[currentIndex] : undefined
  const activeVideo = feedActiveVideo ?? directActiveVideo

  // Search the feed for the target capsule once it loads.
  useEffect(() => {
    if (indexResolved || !capsuleid || videos.length === 0) return
    const index = videos.findIndex(v => v.post.uri.endsWith(capsuleid))
    if (index >= 0) {
      setCurrentIndex(index)
      currentIndexRef.current = index
      setCapsuleFoundInFeed(true)
      committedRef.current = committedRef.current || !!autoPlay
    }
    setIndexResolved(true)
  }, [capsuleid, videos, indexResolved, autoPlay])

  // Fast-path dead end: the direct post resolved but carries no video embed
  // (or could not be fetched) — the capsule is unplayable no matter what the
  // feed contains. Resolve the index immediately instead of waiting on the
  // capsules-feed request, which can stall and leave the shell spinning on an
  // empty loader with no escape.
  const directPostUnplayable =
    !!postAtUri && isDirectPostFetched && !directActiveVideo
  useEffect(() => {
    if (indexResolved || !directPostUnplayable) return
    setIndexResolved(true)
  }, [directPostUnplayable, indexResolved])

  // Dead-end guard: once the feed loaded and nothing playable resolved (a
  // capsule card whose video never attached, a post that can't be fetched),
  // show a message + escape instead of a silent black screen. Also wait for
  // the direct-post fast path so genuine opens don't flash the message while
  // their post is still loading. (Once the capsule IS in the feed the direct
  // post is only needed for the quiz record, so it never gates the pager —
  // otherwise stepping capsules re-fired it and flashed a full-screen loader.)
  const waitingForDirectPost = !!postAtUri && !isDirectPostFetched
  const playerHasNoVideo =
    indexResolved &&
    !activeVideo &&
    !waitingForDirectPost &&
    // The feed gates the message so a capsule found in the feed never flashes
    // it while the feed is still loading; the direct fast path alone settles
    // it when the post resolved without a video.
    (data !== undefined || directPostUnplayable)

  // Fetch the raw capsule record — the authoritative source for themeId and
  // behaviorSettings, which the feed view may omit.
  const {data: rawCapsule, isFetched: isRawCapsuleFetched} =
    useCapsuleByPostQuery(
      activeVideo?.post.uri ?? '',
      activeVideo?.post.author.did ?? '',
    )

  const coverStartS = useMemo(() => {
    const ms = rawCapsule?.coverStartMs
    return typeof ms === 'number' && ms > 0 ? ms / 1000 : 0
  }, [rawCapsule])
  const coverEndS =
    coverStartS > 0 ? coverStartS + CAPSULE_COVER_MAX_SECONDS : 0
  const coverWindowRef = useRef({startS: 0, endS: 0})
  coverWindowRef.current = {startS: coverStartS, endS: coverEndS}

  // ── Mode state (mode A/B are presentation states of the SAME capsule) ─────
  //  - "scroll" (mode A): video already playing muted, mobile-feed front with
  //    the Play call-to-action + actions/reply. The user picks a capsule.
  //  - "playing" (mode B): the user pressed Play — the skip system replaces
  //    the actions, the pager locks (no scroll, no arrows) and the video
  //    completes (→ quiz). Play is the ONLY way into this presentation: the
  //    capsule must be completed once started.
  // The Play CTA switches the presentation state; it is never navigation.
  const [isPlayingMode, setIsPlayingMode] = useState(!!autoPlay)
  const isPlayingModeRef = useRef(false)
  useEffect(() => {
    isPlayingModeRef.current = isPlayingMode
  }, [isPlayingMode])

  // True once the user actually pressed Play (or the capsule was opened with
  // autoPlay). Cleared on capsule change / quiz close so the ONLY way into
  // the playing presentation is the Play button (no momentum carry-over).
  const committedRef = useRef(!!autoPlay)

  // Explicit audio actions (volume control, Play CTA) lift the deterministic
  // start-silent policy for later capsules in this session.
  const [userAudioOn, setUserAudioOn] = useState(false)

  // Mirror of the shared master audio state (lib/sounds) — re-renders the
  // controller so the effective mute (mute icon + the video element) follows
  // changes made by ANY surface: the quiz player, the volume control, here.
  const [masterAudio, setMasterAudio] = useState(() => ({
    muted: getMuted(),
    volume: getMasterVolume(),
  }))
  useEffect(() => subscribeMaster(setMasterAudio), [])

  const [isShowingQuiz, setIsShowingQuiz] = useState(false)
  const isShowingQuizRef = useRef(false)
  useEffect(() => {
    isShowingQuizRef.current = isShowingQuiz
  }, [isShowingQuiz])

  // Ended without Play: the Play CTA becomes a message (play / swipe up for
  // the next one), then auto-scrolls to the next capsule.
  const [endedHint, setEndedHint] = useState(false)
  const endedHintRef = useRef(false)
  useEffect(() => {
    endedHintRef.current = endedHint
  }, [endedHint])
  const [hintSeconds, setHintSeconds] = useState<number | null>(null)

  const autoAdvancePossible =
    (capsuleFoundInFeed && currentIndex < videos.length - 1) ||
    hasNextPage ||
    (!capsuleFoundInFeed && videos.length > 0)

  // Exit confirmation: while the video plays, pressing ✕ pauses everything
  // and asks whether the close was a mistake (continue) or intentional (exit).
  const [isPromptOpen, setIsPromptOpen] = useState(false)

  // Transport state (paused → the Play control stays visible).
  const [videoPaused, setVideoPaused] = useState(false)

  // False while the active video cannot play yet (initial load, a mid-play
  // buffer stall) — the page shows the blurred-thumbnail + white-spinner
  // loading overlay. The engine reports readiness through its callback; a
  // capsule change starts with the old value and the engine immediately
  // corrects it once the new element/player binds.
  const [videoReady, setVideoReady] = useState(true)

  // ── Playback progress + skip countdown ────────────────────────────────────
  const [progress, setProgress] = useState(0)
  const [skipStage, setSkipStage] = useState<CapsuleSkipStage>('hidden')
  const [skipCountdown, setSkipCountdown] = useState(0)
  const skipStageRef = useRef<CapsuleSkipStage>('hidden')
  useEffect(() => {
    skipStageRef.current = skipStage
  }, [skipStage])

  const updateProgress = useCallback(
    (currentTime: number, dur: number) => {
      if (!Number.isFinite(dur) || dur <= 0) return
      // Cover window: seek to the chosen minute and hand off to the quiz at the window's edge.
      if (IS_WEB) {
        const {startS, endS} = coverWindowRef.current
        if (endS > 0) {
          if (currentTime > 0 && currentTime < startS - 0.5) {
            const video = engine.webVideoRef.current
            if (video) video.currentTime = startS
            return
          }
          if (
            currentTime >= endS &&
            isPlayingModeRef.current &&
            !isShowingQuizRef.current
          ) {
            const video = engine.webVideoRef.current
            if (video) video.pause()
            setIsShowingQuiz(true)
            return
          }
        }
      }
      setProgress(Math.min(1, Math.max(0, currentTime / dur)))
      if (!isPlayingModeRef.current || isShowingQuizRef.current) return
      // The countdown is always visible and counts the seconds remaining until
      // the skip becomes available (75% of the video). At 75% it becomes
      // active. It is derived from timeupdate — pausing the video freezes it,
      // resuming continues it (no second timer to desync).
      const skipTarget = dur * SKIP_THRESHOLD
      const remainingToSkip = skipTarget - currentTime
      if (remainingToSkip > 0) {
        setSkipCountdown(Math.ceil(remainingToSkip))
        if (skipStageRef.current !== 'countdown') {
          skipStageRef.current = 'countdown'
          setSkipStage('countdown')
        }
      } else if (skipStageRef.current !== 'active') {
        skipStageRef.current = 'active'
        setSkipStage('active')
        setSkipCountdown(0)
      }
    },
    [setProgress, setSkipCountdown, setSkipStage],
  )

  // Hiding the skip UI is part of every exit from the playing presentation:
  // reaching the quiz (natural end, skip, play-from-ended) and closing the
  // quiz (so a later Play on the same capsule restarts the countdown instead
  // of presenting an instantly-active Skip).
  const resetSkipState = useCallback(() => {
    skipStageRef.current = 'hidden'
    setSkipStage('hidden')
    setSkipCountdown(0)
  }, [])

  // The video finished without Play → show the ended message.
  const handleVideoEnd = useCallback(() => {
    if (isShowingQuizRef.current) return
    if (isPlayingModeRef.current) {
      setIsPromptOpen(false)
      resetSkipState()
      setIsShowingQuiz(true)
      return
    }
    if (endedHintRef.current) return
    endedHintRef.current = true
    setEndedHint(true)
    setHintSeconds(
      currentIndex < videos.length - 1 || hasNextPage
        ? HINT_AUTOSCROLL_SECONDS
        : null,
    )
  }, [currentIndex, videos.length, hasNextPage])

  // Play from the ended message → straight to the quiz (video already ended).
  const onPlayFromEnded = useCallback(() => {
    committedRef.current = true
    setUserAudioOn(true)
    setIsPromptOpen(false)
    endedHintRef.current = false
    setEndedHint(false)
    setHintSeconds(null)
    resetSkipState()
    setIsPlayingMode(true)
    setIsShowingQuiz(true)
  }, [resetSkipState])

  // ── Media engine ──────────────────────────────────────────────────────────
  // Direction: controller → engine (identity data, raw media ops); engine →
  // controller (progress/end/paused events through latest-ref callbacks).
  const engine = useMediaEngine({
    openingUri,
    openingRkey: capsuleid ?? '',
    pendingStartTime,
    onProgress: updateProgress,
    onEnded: handleVideoEnd,
    onPausedChange: setVideoPaused,
    onReadyChange: setVideoReady,
  })

  // (web) Bind the engine to the active page's element; (native) load the
  // active capsule into the player. The engine is told which URI is active
  // first so a resume seek can never land on the wrong capsule.
  useEffect(() => {
    engine.setActiveUri(activeVideo?.post.uri ?? null)
    if (IS_WEB) {
      engine.syncWebElement()
      return () => engine.unbindWebElement()
    }
    engine.replaceSource(activeVideo?.video.playlist)
  }, [activeVideo, engine])

  // Native: background-buffer the next capsule so the next swipe starts
  // playing instantly (TikTok-style preload).
  useEffect(() => {
    if (IS_WEB) return
    engine.preloadNext(videos[currentIndex + 1]?.video.playlist ?? null)
  }, [videos, currentIndex, engine])

  // Native: poll twice per second only when a cover window exists.
  useEffect(() => {
    if (IS_WEB || coverEndS <= 0) return
    engine.player.timeUpdateEventInterval = 0.5
  }, [engine.player, coverEndS])

  useEventListener(engine.player, 'timeUpdate', ({currentTime}) => {
    if (IS_WEB || !isPlayingModeRef.current || isShowingQuizRef.current) return
    const {startS, endS} = coverWindowRef.current
    if (endS <= 0) return
    if (currentTime > 0 && currentTime < startS - 0.5) {
      engine.player.seekBy(startS - currentTime)
      return
    }
    if (currentTime >= endS) {
      engine.player.pause()
      setIsShowingQuiz(true)
    }
  })

  const resetCapsuleState = useCallback(() => {
    committedRef.current = false
    setIsPlayingMode(false)
    setIsShowingQuiz(false)
    setProgress(0)
    resetSkipState()
    setIsPromptOpen(false)
    endedHintRef.current = false
    setEndedHint(false)
    setHintSeconds(null)
    setVideoPaused(false)
  }, [resetSkipState])

  // ── Shareable URL: keep it on the capsule now playing ─────────────────────
  const updateParamsForCapsule = useCallback(
    (video: FeedVideo) => {
      navigation.dispatch(
        CommonActions.replaceParams({
          capsuleid: video.post.uri.split('/').pop() || '',
          authorDid: video.post.author.did,
          source: route.params.source,
        }),
      )
    },
    [navigation, route.params.source],
  )

  // ── Capsule navigation (pager moves one capsule step) ─────────────────────
  const goToCapsule = useCallback(
    (nextIdx: number) => {
      const len = videos.length
      if (capsuleFoundInFeed && nextIdx >= len) {
        // End of the loaded feed — ask for more; if none, stop playback.
        if (hasNextPage) {
          fetchNextPage()
          return
        }
        if (isPlayingModeRef.current) {
          setIsPlayingMode(false)
          setIsShowingQuiz(false)
        }
        return
      }
      if (nextIdx < 0 || (capsuleFoundInFeed && nextIdx >= len)) return
      setCurrentIndex(nextIdx)
      currentIndexRef.current = nextIdx
      resetCapsuleState()
      if (capsuleFoundInFeed) {
        const nextVideo = videos[nextIdx]
        if (nextVideo) updateParamsForCapsule(nextVideo)
        if (nextIdx > len - 5 && hasNextPage) fetchNextPage()
      }
    },
    [
      videos,
      hasNextPage,
      fetchNextPage,
      capsuleFoundInFeed,
      updateParamsForCapsule,
      resetCapsuleState,
    ],
  )

  // User-driven pager change (drag crossed the threshold / settled).
  const onPagerPageChange = useCallback(
    (idx: number) => {
      if (idx === currentIndexRef.current) return
      goToCapsule(idx)
    },
    [goToCapsule],
  )

  // Dead-end escape: jump into the first playable feed capsule.
  const escapeToFeed = useCallback(() => {
    if (videos.length === 0) {
      if (hasNextPage) fetchNextPage()
      return
    }
    setCapsuleFoundInFeed(true)
    setIndexResolved(true)
    goToCapsule(0)
  }, [videos, hasNextPage, fetchNextPage, goToCapsule])

  const onNextCapsule = useCallback(() => {
    if (capsuleFoundInFeed) goToCapsule(currentIndexRef.current + 1)
  }, [capsuleFoundInFeed, goToCapsule])

  const onPreviousCapsule = useCallback(() => {
    goToCapsule(currentIndexRef.current - 1)
  }, [goToCapsule])

  // ── Transport actions (playback only — never touch audio policy) ─────────
  // A play/pause gesture on an ENDED video in the scroll presentation (mode A
  // — Play never pressed) REPLAYS the video: the capsule itself only ever
  // starts from the blue Play CTA (onPressPlayCapsule in scroll mode, the
  // "Play capsule" button in the ended message). Tapping the video surface is
  // video play/pause, never capsule launch — the video simply restarts (still
  // under the same scroll-mode presentation and audio policy).
  const replayVideo = useCallback(() => {
    endedHintRef.current = false
    setEndedHint(false)
    setHintSeconds(null)
    setProgress(0)
    engine.seekTo(0)
    engine.play()
  }, [engine])

  const togglePause = useCallback(() => {
    if (engine.isEnded()) {
      // Ended in the scroll presentation → replay the video. In the playing
      // presentation the ended video already opened the quiz (handleVideoEnd),
      // so the tap must not do anything else here.
      if (!isPlayingModeRef.current && !isShowingQuizRef.current) {
        replayVideo()
      }
      return
    }
    if (engine.isPaused()) engine.play()
    else engine.pause()
  }, [engine, replayVideo])

  const seekBy = useCallback(
    (delta: SeekDelta) => {
      if (!activeVideo) return
      const next = engine.seekByDelta(delta)
      if (next) updateProgress(next.currentTime, next.duration)
    },
    [activeVideo, engine, updateProgress],
  )

  // ── Play (mode A → B presentation switch on the SAME video) ───────────────
  // The video is ALREADY playing when the capsule opened (it started when the
  // user tapped the feed card). Play therefore never restarts it: it commits
  // to the capsule, unmutes and lets the video complete with the skip system.
  const onPressPlayCapsule = useCallback(() => {
    committedRef.current = true
    setUserAudioOn(true)
    setIsPromptOpen(false)
    endedHintRef.current = false
    setEndedHint(false)
    setHintSeconds(null)
    setIsPlayingMode(true)
    engine.unmuteAndPlay()
  }, [engine])

  const onSkip = useCallback(() => {
    if (skipStageRef.current !== 'active') return
    skipStageRef.current = 'hidden'
    setSkipStage('hidden')
    engine.pause()
    setIsPromptOpen(false)
    setIsShowingQuiz(true)
  }, [engine, setSkipStage, setIsPromptOpen, setIsShowingQuiz])

  // ── Audio: reuse the quiz volume control + lib/sounds master state ────────
  // The control already wrote the change into the shared master state; the
  // engine mirrors it onto the active media element/player through its master
  // subscription. Here we only track that the user asked for sound so the
  // start-muted policy lifts for the rest of this session.
  const onAudioStateChange = useCallback(
    (state: {muted: boolean; volume: number}) => {
      if (!state.muted && state.volume > 0) setUserAudioOn(true)
    },
    [],
  )

  const pauseAndAsk = useCallback(() => {
    engine.pause()
    setIsPromptOpen(true)
  }, [engine])

  const resumeVideo = useCallback(() => {
    setIsPromptOpen(false)
    engine.play()
  }, [engine])

  const exitPlayer = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.navigate('Feeds')
    }
  }, [navigation])

  const closeQuiz = useCallback(() => {
    committedRef.current = false
    setIsShowingQuiz(false)
    setIsPlayingMode(false)
    setIsPromptOpen(false)
    resetSkipState()
  }, [resetSkipState])

  // Auto-scroll to the next capsule after HINT_AUTOSCROLL_SECONDS of inaction.
  const autoScrollToNext = useCallback(() => {
    endedHintRef.current = false
    setEndedHint(false)
    setHintSeconds(null)
    if (capsuleFoundInFeed) {
      goToCapsule(currentIndexRef.current + 1)
    } else if (videos.length > 0) {
      escapeToFeed()
    } else if (hasNextPage) {
      fetchNextPage()
    }
  }, [
    capsuleFoundInFeed,
    goToCapsule,
    videos,
    escapeToFeed,
    hasNextPage,
    fetchNextPage,
  ])

  useEffect(() => {
    if (!endedHint || hintSeconds == null || !autoAdvancePossible) return
    if (!isFocused) return
    if (hintSeconds === 0) {
      autoScrollToNext()
      return
    }
    const id = setTimeout(() => {
      setHintSeconds(s => (s == null ? s : Math.max(0, s - 1)))
    }, 1000)
    return () => clearTimeout(id)
  }, [endedHint, hintSeconds, autoAdvancePossible, autoScrollToNext, isFocused])

  // Native: play while focused; pause when the screen loses focus.
  useFocusEffect(
    useCallback(() => {
      if (!IS_WEB && !isShowingQuiz) {
        engine.play()
      }
      return () => {
        engine.pause()
      }
    }, [engine, isShowingQuiz]),
  )

  // ── Desktop input: keyboard stepping (touch scrolls the pager; the wheel
  //    steps one capsule per gesture inside the web pager itself) ──────────
  useEffect(() => {
    if (!IS_WEB) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return
      }
      const locked = isPagerLocked(
        isPlayingModeRef.current,
        isShowingQuizRef.current,
        isPromptOpen,
      )
      if (locked) {
        // Locked (play mode / quiz / exit prompt): no navigation key may
        // native-scroll a focused container (the pager itself included) or
        // step the feed — Space/PageDown/PageUp/Home/End/Arrows are all
        // inert. Interactive elements (the shared INTERACTIVE_SELECTOR:
        // buttons, links, inputs, selects) keep their own key behavior, so
        // quiz options and dialog buttons still work.
        const interactive = !!target?.closest?.(INTERACTIVE_SELECTOR)
        if (!interactive) {
          if (
            e.key === 'PageDown' ||
            e.key === 'PageUp' ||
            e.key === 'Home' ||
            e.key === 'End' ||
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === ' '
          ) {
            e.preventDefault()
          }
        }
        // Transport stays available in play mode: seek + pause only.
        if (isPlayingModeRef.current) {
          if (e.key === 'ArrowRight') {
            e.preventDefault()
            seekBy(5)
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault()
            seekBy(-5)
          } else if (e.key === ' ' || e.key === 'k') {
            e.preventDefault()
            togglePause()
          }
        }
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        onNextCapsule()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        onPreviousCapsule()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        seekBy(5)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        seekBy(-5)
      } else if (e.key === ' ' || e.key === 'k') {
        e.preventDefault()
        togglePause()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isPromptOpen, onNextCapsule, onPreviousCapsule, seekBy, togglePause])

  // ── Build the raw capsule record for the quiz/overlay of the ACTIVE page ──
  const capsuleModel: CapsuleModel | null = useMemo(() => {
    const raw = activeVideo?.capsule
    if (!raw && !rawCapsule) return null
    return {
      id:
        (raw?.uri as string | undefined) ??
        rawCapsule?.uri ??
        activeVideo?.post.uri ??
        'unknown',
      title: rawCapsule?.title ?? (raw?.title as string | undefined) ?? '',
      description: (raw?.description as string | undefined) ?? '',
      creatorId: activeVideo?.post.author.did ?? '',
      themeId:
        rawCapsule?.themeId ??
        (raw?.themeId as string | undefined) ??
        'classic-quiz',
      status: 'published' as const,
      slides: rawCapsule?.slides ?? (raw?.slides as Slide[] | undefined) ?? [],
      behaviorSettings:
        rawCapsule?.behaviorSettings ??
        (raw?.behaviorSettings as Record<string, unknown> | undefined) ??
        {},
      metadata: {createdAt: '', updatedAt: ''},
      likeCount: 0,
      likedByMe: false,
    }
  }, [activeVideo, rawCapsule])

  const overlayMode: CapsuleOverlayMode = isPlayingMode
    ? 'playing'
    : endedHint
      ? 'ended'
      : 'scroll'

  const skipProgress = Math.min(
    1,
    SKIP_THRESHOLD > 0 ? progress / SKIP_THRESHOLD : 1,
  )

  // Start-muted policy: muted until the user presses Play or touches the
  // volume control (web only; the native feed model plays with sound). The
  // engine is told the policy separately so it can combine it with the shared
  // master mute — a global mute always keeps the video silent as well.
  const policyMuted = !isPlayingMode && !userAudioOn
  useEffect(() => {
    engine.setPolicyMuted(policyMuted)
  }, [engine, policyMuted])

  // Effective muted state the UI and the video element should reflect: on web
  // the start-muted policy applies until the user acts; a global mute (set in
  // the quiz or the volume control) mutes everywhere, including native where
  // the feed model plays with sound by default.
  const mutedPolicy = (IS_WEB ? policyMuted : false) || masterAudio.muted

  return {
    engine,
    videos,
    // The $Typed brand from the direct-post query isn't assignable to
    // VideoItem's plain View — same runtime object.
    activeVideo: activeVideo as FeedVideo | undefined,
    capsuleModel,
    currentIndex,
    capsuleFoundInFeed,
    isPlayingMode,
    isShowingQuiz,
    isPromptOpen,
    isRawCapsuleFetched,
    // Play mode locks the pager too: after Play the capsule must complete
    // (no scroll, no arrows) — only the page chrome stays interactive.
    pagerLocked: isPagerLocked(isPlayingMode, isShowingQuiz, isPromptOpen),
    playerHasNoVideo,
    autoAdvancePossible,
    hasNextPage,
    feedFeedback,
    videoPaused,
    hintSeconds,
    overlayMode,
    skipStage,
    skipCountdown,
    skipProgress,
    progress,
    videoReady,
    mutedPolicy,
    onSeek: seekBy,
    onTogglePause: togglePause,
    onLongPressChange: engine.setLongPress,
    onPlayFromEnded,
    onSkip,
    onPressPlayCapsule,
    onAudioStateChange,
    onPagerPageChange,
    onNextCapsule,
    onPreviousCapsule,
    pauseAndAsk,
    resumeVideo,
    exitPlayer,
    closeQuiz,
    escapeToFeed,
    onVideoError: () => setVideoReady(true),
  }
}
